import type { GameContext } from '../core/context';
import { distance, type V2 } from '../core/math';
import { CAB_FIRM } from '../activities/cab';
import { contactPrice } from '../contacts/perks';

/**
 * "Ring for a minicab" (docs/MISSIONS.md §Minicab): a short dip to black and Kestrel Cars has dropped you
 * at the kerb by a job's start in a plain little car, for a small fare. It saves the dead walk back after a
 * blown job (the result sheet: "Minicab back to the start") and the long trek to the next job's giver (the
 * book's JOBS page). It never goes to an objective inside a job — only to where the job is picked up, which
 * is before its first objective — and nobody comes out for a fare the police are after.
 *
 * The rules are pure (`cabCheck`, `cabDropSpot`) so they can be tested without a browser.
 */

export const MINICAB_FARE = 15;
/** Closer than this, it's a walk: the minicab isn't offered. */
export const MINICAB_MIN_DISTANCE = 150;
/** The car is left at a kerb spot this far from the destination (never on top of the giver). */
export const DROP_MIN = 6;
export const DROP_MAX = 45;
/** The car Kestrel Cars leaves you: an ordinary little saloon (a Mini-like compact). */
export const MINICAB_SPEC = 'compact';

export type CabRefusal = 'wanted' | 'online' | 'onJob' | 'broke' | 'near';

export interface CabSituation {
  /** Wanted level. */
  heat: number;
  online: boolean;
  /** A job is running (the result sheet's ride is checked between the failure and the restart). */
  onJob: boolean;
  money: number;
  /** Metres from the player to the destination. */
  distance: number;
  /** The fare after Dot's standing (contact ladder, WP-X1); absent = MINICAB_FARE. */
  fare?: number;
}

/** The fare after your standing with Dot at Kestrel Cars (contact ladder, WP-X1): £15, £10, or nothing. */
export function minicabFare(ctx: GameContext): number {
  return contactPrice(ctx, 'minicab', MINICAB_FARE);
}

/** "£15", or "on Dot" when the ride is free. */
export const fareText = (fare: number): string => (fare > 0 ? `£${fare}` : 'free, on Dot');

/** Null when the ride is on; otherwise why not (the first rule that says no). */
export function cabCheck(s: CabSituation): CabRefusal | null {
  if (s.online) return 'online';
  if (s.heat > 0) return 'wanted';
  if (s.onJob) return 'onJob';
  if (s.distance < MINICAB_MIN_DISTANCE) return 'near';
  if (s.money < (s.fare ?? MINICAB_FARE)) return 'broke';
  return null;
}

/** Plain words for a refusal (a toast). */
export const CAB_REFUSAL_TEXT: Record<CabRefusal, string> = {
  online: 'Minicabs are for offline play.',
  wanted: `${CAB_FIRM} won’t send a car while the police are after you. Lose them first.`,
  onJob: 'Not in the middle of a job.',
  near: 'It’s only round the corner — walk it.',
  broke: `A minicab costs £${MINICAB_FARE}. You haven’t got it.`,
};

/**
 * Where to leave the car: the kerb spot nearest the destination that is at least `DROP_MIN` m from it
 * (and no more than `DROP_MAX`), and free (`taken` says when a car is already parked there). Falls back to
 * a point beside the destination.
 */
export function cabDropSpot(spots: readonly { pos: V2; heading: number }[], dest: V2, taken: (p: V2) => boolean): { pos: V2; heading: number } {
  let best: { pos: V2; heading: number } | null = null;
  let bestD = Infinity;
  for (const s of spots) {
    const d = distance(s.pos, dest);
    if (d < DROP_MIN || d > DROP_MAX || d >= bestD || taken(s.pos)) continue;
    best = s;
    bestD = d;
  }
  return best ?? { pos: { x: dest.x + DROP_MIN, z: dest.z }, heading: 0 };
}

/**
 * The ride itself: put the player in a fresh car at the drop spot near `dest` (released at once — it is
 * nobody's job car, and it isn't stolen). Charges nothing; the caller checks the rules and takes the fare.
 */
export function cabRide(ctx: GameContext, dest: V2): void {
  const vehicles = ctx.services.get('vehicles');
  const player = ctx.services.get('player');
  const spot = cabDropSpot(ctx.services.get('layout').parkedSpots, dest, (p) => vehicles.query(p, 3.5).some((v) => v !== player.vehicle));
  if (player.mode === 'driving') player.exitVehicle();
  player.teleport(spot.pos);
  // The car is yours (no theft to get in), so the abandoned-car cull never takes it: Kestrel Cars collect the
  // last one when they drop the next, or every ride would leave a car standing for the rest of the session.
  // (Not once it is somebody's: made your motor at Halloran's, kept at the lockup — any tag.)
  const last = lastCab.get(ctx);
  const old = last === undefined ? undefined : vehicles.get(last);
  if (old && old !== player.vehicle && old.occupant === null && old.ownedByPlayer && old.spec.id === MINICAB_SPEC && old.tags.size === 0) vehicles.despawn(old);
  const car = vehicles.spawn(MINICAB_SPEC, spot.pos, { heading: spot.heading, ownedByPlayer: true });
  player.enterVehicle(car, true);
  lastCab.set(ctx, car.id);
}

/** The minicab each game last left the player in (vehicle id). */
const lastCab = new WeakMap<GameContext, number>();

/**
 * A short dip to black around `midway` (the ride happens while the screen is dark). Headless (tests, no
 * UI root) it simply runs `midway` at once.
 */
export function dipToBlack(ctx: GameContext, caption: string, midway: () => void): void {
  const root = ctx.uiRoot?.querySelector('.bh-ui') as HTMLElement | null | undefined;
  if (!root || typeof window === 'undefined') {
    midway();
    return;
  }
  const dip = document.createElement('div');
  dip.className = 'bh-dip';
  const cap = document.createElement('div');
  cap.className = 'cap';
  cap.textContent = caption;
  dip.appendChild(cap);
  root.appendChild(dip);
  // Next frame: fade in, ride while dark, fade out, gone.
  window.requestAnimationFrame(() => dip.classList.add('on'));
  window.setTimeout(() => {
    midway();
    window.setTimeout(() => dip.classList.remove('on'), 650);
    window.setTimeout(() => dip.remove(), 1100);
  }, 420);
}
